import type { CSSProperties, ReactElement } from 'react';
import { COULEURS, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import { cleDestinataire, corpsPartage, destinatairesCoches, type CorpsPartage, type DestinatairePartage } from './modele.js';

export interface PropsAlertePartage {
  etiquette: { id: string; label: string };
  /** Personnes proposées : aucune n'est retenue d'office. */
  destinataires: readonly DestinatairePartage[];
  /** Clés (voir `cleDestinataire`) des personnes cochées par l'équipe. */
  selection: readonly string[];
  /** Raison pour laquelle le partage est impossible (liste illisible), sinon null. */
  indisponible: string | null;
  onBasculer: (cle: string) => void;
  onConfirmer: (etiquetteId: string, corps: CorpsPartage) => void | Promise<void>;
  onAnnuler: () => void;
}

const styleAlerte: CSSProperties = {
  border: `2px solid ${COULEURS.ambre}`,
  background: COULEURS.ambreFond,
  color: COULEURS.marineFonce,
  borderRadius: 8,
  padding: 16,
  margin: '12px 0',
};
const styleListe: CSSProperties = { listStyle: 'none', margin: '8px 0', padding: 0 };
const styleCase: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, minHeight: 44, cursor: 'pointer' };
const styleActions: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 };
const styleDesactive: CSSProperties = { opacity: 0.5, cursor: 'not-allowed' };

const personnes = (n: number): string => `${n} personne${n > 1 ? 's' : ''}`;

/**
 * Alerte de partage hors équipe : l'équipe COCHE chaque destinataire (aucune case cochée d'office) et seules les
 * personnes cochées et affichées sont communiquées, journalisées avec cette liste (le contrat refuse un partage dont un
 * destinataire n'a pas été montré). Composant sans état : la sélection vient de la vue.
 */
export function AlertePartage({
  etiquette,
  destinataires,
  selection,
  indisponible,
  onBasculer,
  onConfirmer,
  onAnnuler,
}: PropsAlertePartage): ReactElement {
  const coches = destinatairesCoches(destinataires, selection);
  const n = coches.length;
  const confirmable = n > 0;
  return (
    <section role="alertdialog" aria-modal="true" aria-labelledby="alerte-partage-titre" data-alerte="partage" style={styleAlerte}>
      <h3 id="alerte-partage-titre" style={{ margin: 0, fontSize: 18 }}>
        Partager hors de l’équipe MAAT
      </h3>
      {indisponible !== null ? (
        <p role="status">{indisponible}</p>
      ) : destinataires.length === 0 ? (
        <p>Aucun destinataire hors de l’équipe n’est proposé pour le moment. Rien ne sera partagé.</p>
      ) : (
        <>
          <p>{`« ${etiquette.label} » ne sera communiqué qu’aux personnes que vous cochez ci-dessous, hors de l’équipe MAAT :`}</p>
          <ul style={styleListe}>
            {destinataires.map((d) => {
              const cle = cleDestinataire(d);
              return (
                <li key={cle}>
                  <label style={styleCase}>
                    <input
                      type="checkbox"
                      aria-label={d.displayName}
                      value={cle}
                      checked={selection.includes(cle)}
                      onChange={() => onBasculer(cle)}
                      style={{ width: 24, height: 24 }}
                    />
                    <span>{d.displayName}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          <p>Ce partage est journalisé avec les noms cochés.</p>
        </>
      )}
      <div style={styleActions}>
        {indisponible === null && destinataires.length > 0 ? (
          <button
            type="button"
            disabled={!confirmable}
            style={confirmable ? styleBoutonPrincipal : { ...styleBoutonPrincipal, ...styleDesactive }}
            onClick={() => {
              if (confirmable) void onConfirmer(etiquette.id, corpsPartage(coches));
            }}
          >
            {confirmable ? `Confirmer le partage avec ${personnes(n)}` : 'Confirmer le partage'}
          </button>
        ) : null}
        <button type="button" style={styleBouton} onClick={onAnnuler}>
          Annuler
        </button>
      </div>
    </section>
  );
}
